import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useNotifications } from '../../components/common/NotificationSystem';
import ProfessionalLoader from "../../components/common/ProfessionalLoader";
import { authService } from '../../services/authService';
import Logo from '../../components/common/Logo';
import './Auth.css';

const EmailVerification = () => {
  const [, setIsLoading] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('pending'); // 'pending', 'verifying', 'success', 'error'
  const [error, setError] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSuccess, showError } = useNotifications();

  const email = location.state?.email || 'your email';
  const token = searchParams.get('token');

  const handleAutoVerify = useCallback(async (token) => {
    setIsLoading(true);
    setVerificationStatus('verifying');
    setError('');

    try {
      const response = await authService.verifyEmail(token);
      if (response.success) {
        setVerificationStatus('success');
        showSuccess('Email verified successfully! You can now sign in.', 'Verification Complete');
        // Redirect to login after a short delay
        setTimeout(() => {
          navigate('/login', {
            state: { message: 'Email verified successfully! Please sign in.' }
          });
        }, 2000);
      } else {
        setVerificationStatus('error');
        setError(response.message || 'Verification failed. Please try again.');
        showError(response.message || 'Verification failed', 'Verification Error');
      }
    } catch (error) {
      setVerificationStatus('error');
      setError('Verification failed. Please try again.');
      showError('Verification failed', 'Verification Error');
    } finally {
      setIsLoading(false);
    }
  }, [navigate, showSuccess, showError]);

  useEffect(() => {
    // Auto-verify if token is present in URL
    if (token) {
      handleAutoVerify(token);
    }
  }, [token, handleAutoVerify]);


  // Render different UI based on verification status
  const renderContent = () => {
    if (verificationStatus === 'verifying') {
      return (
        <div className="verification-content">
          <div className="verification-icon">
            <ProfessionalLoader size="large" variant="default" />
          </div>
          <h1>Verifying Your Email</h1>
          <p>Please wait while we verify your email address...</p>
        </div>
      );
    }

    if (verificationStatus === 'success') {
      return (
        <div className="verification-content">
          <div className="verification-icon success">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22,4 12,14.01 9,11.01" />
            </svg>
          </div>
          <h1>Email Verified Successfully!</h1>
          <p>Your email has been verified. You will be redirected to the login page shortly.</p>
          <div className="verification-links">
            <Link to="/login" className="auth-button">Go to Login</Link>
          </div>
        </div>
      );
    }

    if (verificationStatus === 'error') {
      return (
        <div className="verification-content">
          <div className="verification-icon error">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <h1>Verification Failed</h1>
          <p className="error-message">{error}</p>
          <div className="verification-links">
            <Link to="/signup">Back to Sign Up</Link>
            <Link to="/login">Back to Sign In</Link>
          </div>
        </div>
      );
    }

    // Default state - no token in URL
    return (
      <div className="verification-content">
        <div className="auth-header">
          <Logo
            className="auth-logo"
            alt="AFF Logo"
          />
          <h1>Check Your Email</h1>
        </div>
        <p>We've sent a verification link to:</p>
        <p className="email-display">{email}</p>
        <p>Please click the verification link in your email to complete the registration process.</p>


        <div className="verification-links">
          <Link to="/signup">Back to Sign Up</Link>
          <Link to="/login">Back to Sign In</Link>
        </div>
      </div>
    );
  };

  return (
    <div className="auth-container">
      <div className="auth-card verification-card">
        {renderContent()}
      </div>
    </div>
  );
};

export default EmailVerification;
